import _ from 'lodash';

import { CONFIG } from 'constants/index';

import { ValidationResult } from './validation';

interface RequestParams {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: Record<string, unknown> | void;
  secure?: boolean;
}

export type ApiResponse<R, E> = ({ success: true } & R) | ({ success: false } & E);

const CAN_USE_DOM = typeof window !== 'undefined';

/**
 * Http request helper
 */
export const api = {
  async request<R, E>(params: RequestParams) {
    const { url, method, body } = params;
    const baseUrl = CAN_USE_DOM ? `${window.location.origin}/api` : CONFIG.api;
    const query = method === 'GET' && !_.isEmpty(body) ? `?${urlParams(body)}` : '';
    const targetUrl = `${baseUrl}${url}${query}`;
    const headers = { 'Content-Type': 'application/json; charset=utf-8' };
    const options: RequestInit = { mode: 'cors' };

    options.method = method;
    options.headers = headers;
    if (method !== 'GET') options.body = JSON.stringify(body);

    const response = await fetch(targetUrl, options);
    const jsonResp = await response.json();
    const result: ApiResponse<R, E> = {
      ...jsonResp,
    };

    return result;
  },
};

export const urlParams = (obj: RequestParams['body']) => {
  if (obj) {
    return Object.entries(obj)
      .map(([key, val]) => `${key}=${val}`)
      .join('&');
  }
  return '';
};

/**
 * Endpoint wrap helper
 */
export type Endpoint<Params extends Record<string, unknown> | void, Result, Error> = (params: Params) => {
  args: {
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    url: string;
    body?: Params;
    secure?: boolean;
  };
  invoke: () => Promise<ApiResponse<Result, Error>>;
};

export const endpoint = <Params extends Record<string, unknown> | void, Result, Error>(fetchArgs: {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  url: (params: Params) => string;
}): Endpoint<Params, Result, Error> => {
  return (params) => ({
    args: {
      method: fetchArgs.method,
      url: `${fetchArgs.url(params)}${fetchArgs.method === 'GET' ? '?' + urlParams(params) : ''}`,
      body: fetchArgs.method !== 'GET' ? params : undefined,
    },
    invoke() {
      return api.request(this.args);
    },
  });
};

/**
 * Endpoint errors list
 */
export enum ERROR {
  INVALID_DATA = 'INVALID_DATA',
  ACCESS_DENIED = 'ACCESS_DENIED',
  UNEXPECTED_ERROR = 'UNEXPECTED_ERROR',
  UNATHORIZED_REQUEST = 'UNATHORIZED_REQUEST',
  ITEM_ALREADY_EXISTS = 'ITEM_ALREADY_EXISTS',
}

/**
 * Common error type
 */
export type EndpointError =
  | {
      code: ERROR.INVALID_DATA;
      validation: ValidationResult;
    }
  | {
      code: ERROR.ACCESS_DENIED;
    }
  | {
      code: ERROR.UNEXPECTED_ERROR;
      details: unknown;
    };
